<template>

  <div>
    <b-row
      class="content-header"
    >

      <!-- Content Left -->
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h2 class="custom-header-title float-left pr-1 mb-0">
              Retail Orders
            </h2>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row class="mb-1">
      <b-col cols="12">
        <b-button
          v-for="tab in statusTabs"
          :key="tab.code"
          :variant="activeTab === tab.code ? 'primary' : 'outline-primary'"
          class="status-tab-button mr-1 mb-1"
          @click="activeTab = tab.code"
        >
          {{ tab.title }} <b-badge
            pill
            :variant="activeTab === tab.code ? 'light' : 'light-primary'"
            :class="{ 'active-tab-badge': activeTab === tab.code }"
            class="ml-50"
          >
            {{ tab.count }}
          </b-badge>
        </b-button>
      </b-col>
    </b-row>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="4"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-form-checkbox
              class="mr-1"
              :checked="isAllSelected"
              @change="toggleSelectAll"
            />
            <b-dropdown
              v-if="canViewThisAction('update', 'RetailOrder')"
              variant="primary"
              text="Bulk actions"
            >
              <b-dropdown-item
                :disabled="selectedOrderIds.length === 0"
                @click="emailTaxInvoice(selectedOrderIds)"
              >
                Email Tax Invoice to Customer
              </b-dropdown-item>
            </b-dropdown>
            <span class="text-muted ml-1">Showing {{ dataMeta.from }} - {{ dataMeta.to }} of {{ dataMeta.of }}</span>
          </b-col>
          <b-col
            cols="12"
            md="8"
            class="d-flex flex-wrap align-items-center justify-content-md-end"
          >
            <b-input-group class="search-input mr-1 mb-1 mb-md-0 search_group">
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" /> Search
              </b-input-group-prepend>
              <b-form-input
                v-model="searchQuery"
                placeholder="Search"
              />
              <b-input-group-append
                v-if="searchQuery"
                is-text
              >
                <feather-icon
                  icon="XIcon"
                  class="cursor-pointer"
                  @click="searchQuery = ''"
                />
              </b-input-group-append>
            </b-input-group>

            <b-form-input
              v-model="orderNoQuery"
              placeholder="Order No."
              class="order-no-input mr-1 mb-1 mb-md-0"
            />

            <b-button
              variant="filter_actions"
              @click="isFilterSidebarActive = true"
            >
              <feather-icon
                icon="FilterIcon"
                size="16"
                class="mr-50"
              />
              Filter
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-table
        class="position-relative min-height-table"
        :items="paginatedOrders"
        responsive
        :fields="tableColumns"
        primary-key="orderNo"
        show-empty
        empty-text="No matching orders found"
      >
        <template #cell(checkbox)="data">
          <b-form-checkbox
            v-model="selectedOrderIds"
            :value="data.item._id"
          />
        </template>

        <template #cell(orderNo)="data">
          <b-link
            class="weight-600 align-middle detail-view-id"
            :to="{ name: 'customers-orders-show', params: { id: data.item._id } }"
          >
            {{ data.item.stringID }}
          </b-link>
        </template>

        <template #cell(relatedRef)="data">
          <div v-if="data.item.relatedServiceRequest || data.item.relatedServiceRequestStringID">
            <b-link
              v-if="data.item.relatedServiceRequest"
              class="weight-600 align-middle detail-view-id"
              :to="{ name: 'operation-service-requests-show', params: { id: data.item.relatedServiceRequest._id } }"
            >
              {{ data.item.relatedServiceRequestStringID || '–' }}
            </b-link>
            <span v-else>{{ data.item.relatedServiceRequestStringID || '–' }}</span><br>
            <small
              v-if="data.item.relatedServiceRequest"
              class="text-muted text-capitalize"
            >{{ data.item.relatedServiceRequest.status }}</small>
          </div>
          <div v-else-if="data.item.relatedBooking || data.item.relatedBookingStringID">
            <b-link
              v-if="data.item.relatedBooking"
              class="weight-600 align-middle detail-view-id"
              :to="{ name: 'appointment-bookings-show', params: { id: data.item.relatedBooking._id } }"
            >
              {{ data.item.relatedBookingStringID || '–' }}
            </b-link>
            <span v-else>{{ data.item.relatedBookingStringID || '–' }}</span><br>
            <small
              v-if="data.item.relatedBooking"
              class="text-muted text-capitalize"
            >{{ data.item.relatedBooking.status ? data.item.relatedBooking.status.replace('-', ' ') : '' }}</small>
          </div>
          <span v-else>–</span>
        </template>

        <template #cell(serviceDateTime)="data">
          {{ data.item.serviceDate ? dateFormat(data.item.serviceDate) : '–' }}<span v-if="data.item.serviceTime">, {{ data.item.serviceTime }}</span>
        </template>

        <template #cell(customer)="data">
          {{ data.item.customerName }}
        </template>

        <template #cell(orderStatus)="data">
          <b-badge
            :variant="`light-${resolveOrderStatusVariant(data.item.orderStatus)}`"
            class="text-capitalize"
          >
            {{ data.item.orderStatus }}
          </b-badge>
        </template>

        <template #cell(paymentStatus)="data">
          <b-badge
            :variant="`light-${resolvePaymentStatusVariant(data.item.paymentStatus)}`"
            class="text-capitalize"
          >
            {{ data.item.paymentStatus }}
          </b-badge>
        </template>

        <template #cell(orderDate)="data">
          {{ dateFormat(data.item.createdAt) }}
        </template>

        <template #cell(total)="data">
          ${{ numberFormat(data.item.total) }}
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="canViewThisAction('show', 'RetailOrder')"
              :to="{ name: 'customers-orders-show', params: { id: data.item._id } }"
            >
              <span class="align-middle ml-50">View</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canViewThisAction('update', 'RetailOrder')"
              @click="emailTaxInvoice([data.item._id])"
            >
              <span class="align-middle ml-50">Email Tax Invoice to Customer</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-card>

    <div
      v-if="filteredOrders.length > perPage"
      class="mx-2 mb-2"
    >
      <b-row>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="12"
          class="d-flex align-items-center justify-content-center pagination-content"
        >

          <b-pagination
            v-model="currentPage"
            :total-rows="filteredOrders.length"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            align="center"
            prev-text="Prev"
            next-text="Next"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
              <span>Prev</span>
            </template>
            <template #next-text>
              <span>Next</span>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>

        </b-col>

      </b-row>
    </div>

    <retail-order-filters
      :is-filter-sidebar-active.sync="isFilterSidebarActive"
      @apply-filter="onApplyFilter"
    />
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BCard, BTable, BBadge, BDropdown, BDropdownItem, BFormCheckbox, BFormInput,
  BInputGroup, BInputGroupPrepend, BInputGroupAppend, BLink, BPagination,
} from 'bootstrap-vue'
import { useUtils as useAclUtils } from '@/libs/acl/custom'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import RetailOrderFilters from './RetailOrderFilters.vue'

const { canViewThisAction } = useAclUtils()

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BCard,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BLink,
    BPagination,
    RetailOrderFilters,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },

  data() {
    return {
      canViewThisAction,
      activeTab: 'All',
      isFilterSidebarActive: false,
      searchQuery: '',
      orderNoQuery: '',
      appliedFilters: null,
      tableColumns: [
        { key: 'checkbox', label: '' },
        { key: 'orderNo', label: 'Order No.', sortable: true },
        { key: 'relatedRef', label: 'Related SR/Appt' },
        { key: 'serviceDateTime', label: 'Service Date/Time', sortable: true },
        { key: 'customer', label: 'Customer', sortable: true },
        { key: 'orderStatus', label: 'Order Status', sortable: true },
        { key: 'paymentStatus', label: 'Payment Status', sortable: true },
        { key: 'orderDate', label: 'Order Date', sortable: true },
        { key: 'total', label: 'Total', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      orders: [],
      selectedOrderIds: [],
      currentPage: 1,
      perPage: Number(process.env.VUE_APP_PER_PAGE_CONTENT) || 10,
      counts: {
        allCount: 0, pendingCount: 0, confirmedCount: 0, completedCount: 0, cancelledCount: 0,
      },
    }
  },

  computed: {
    statusTabs() {
      return [
        { title: 'Pending', code: 'Pending', count: this.counts.pendingCount },
        { title: 'Confirmed', code: 'Confirmed', count: this.counts.confirmedCount },
        { title: 'Completed', code: 'Completed', count: this.counts.completedCount },
        { title: 'Cancelled', code: 'Cancelled', count: this.counts.cancelledCount },
        { title: 'All Orders', code: 'All', count: this.counts.allCount },
      ]
    },
    filteredOrders() {
      let list = this.orders

      if (this.activeTab !== 'All') {
        list = list.filter(o => o.orderStatus === this.activeTab)
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        list = list.filter(o => (o.customerName || '').toLowerCase().includes(query))
      }

      if (this.orderNoQuery) {
        const query = this.orderNoQuery.toLowerCase()
        list = list.filter(o => (o.stringID || '').toLowerCase().includes(query))
      }

      if (this.appliedFilters) {
        const filters = this.appliedFilters

        if (filters.appointmentTypes && filters.appointmentTypes.length) {
          list = list.filter(o => o.relatedBooking && o.relatedBooking.activityType
            && filters.appointmentTypes.includes(o.relatedBooking.activityType.typeName))
        }

        if (filters.serviceTypes && filters.serviceTypes.length) {
          list = list.filter(o => filters.serviceTypes.includes(o.serviceType))
        }

        if (filters.appointmentServiceDate) {
          list = list.filter(o => o.serviceDate && this.dateFormat(o.serviceDate) === filters.appointmentServiceDate)
        }

        if (filters.orderDate) {
          list = list.filter(o => o.createdAt && this.dateFormat(o.createdAt) === filters.orderDate)
        }

        if (filters.paymentStatus) {
          list = list.filter(o => o.paymentStatus === filters.paymentStatus)
        }

        if (filters.appointmentStatus) {
          list = list.filter(o => o.relatedBooking
            && this.normalizeStatus(o.relatedBooking.status) === this.normalizeStatus(filters.appointmentStatus))
        }

        if (filters.srStatus) {
          list = list.filter(o => o.relatedServiceRequest
            && this.normalizeStatus(o.relatedServiceRequest.status) === this.normalizeStatus(filters.srStatus))
        }
      }

      return list
    },
    isAllSelected() {
      return this.filteredOrders.length > 0 && this.filteredOrders.every(o => this.selectedOrderIds.includes(o._id))
    },
    paginatedOrders() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredOrders.slice(start, start + this.perPage)
    },
    dataMeta() {
      const total = this.filteredOrders.length
      const localCount = this.paginatedOrders.length
      return {
        from: this.perPage * (this.currentPage - 1) + (localCount ? 1 : 0),
        to: this.perPage * (this.currentPage - 1) + localCount,
        of: total,
      }
    },
  },
  watch: {
    activeTab() {
      this.currentPage = 1
    },
    searchQuery() {
      this.currentPage = 1
    },
    orderNoQuery() {
      this.currentPage = 1
    },
    appliedFilters() {
      this.currentPage = 1
    },
  },

  mounted() {
    this.fetchRetailOrders()
    this.fetchCounts()
  },

  methods: {
    normalizeStatus(status) {
      return (status || '').toLowerCase().replace(/[-\s]+/g, ' ').trim()
    },
    resolveOrderStatusVariant(status) {
      if (status === 'Confirmed') return 'info'
      if (status === 'Completed') return 'success'
      if (status === 'Cancelled') return 'danger'
      return 'warning'
    },
    resolvePaymentStatusVariant(status) {
      if (status === 'Fully Paid') return 'success'
      if (status === 'Partially Paid') return 'warning'
      if (status === 'Overpaid') return 'info'
      if (status === 'Refunded') return 'secondary'
      return 'secondary'
    },
    onApplyFilter(filters) {
      this.appliedFilters = filters
    },
    fetchRetailOrders() {
      this.$http.get('sales/retail-orders', { params: { perPage: 1000, sortBy: 'createdAt', sortDesc: true } })
        .then(response => {
          this.orders = response.data.retailOrders
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    fetchCounts() {
      this.$http.get('sales/retail-orders/counts')
        .then(response => {
          this.counts = response.data
        })
        .catch(() => {})
    },
    toggleSelectAll(checked) {
      const visibleIds = this.filteredOrders.map(o => o._id)
      this.selectedOrderIds = checked
        ? [...new Set([...this.selectedOrderIds, ...visibleIds])]
        : this.selectedOrderIds.filter(id => !visibleIds.includes(id))
    },
    emailTaxInvoice(orderIds) {
      if (!orderIds.length) return

      this.$http.post('sales/retail-orders/email-invoice', { orderIds })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Tax invoice emailed to customer',
              icon: 'BellIcon',
            },
          })
          this.selectedOrderIds = []
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.search-input {
  max-width: 220px;
}
.order-no-input {
  max-width: 150px;
}

.status-tab-button {
  &.btn-outline-primary {
    border-color: #104D9D;
    color: #104D9D;
  }
  &.btn-primary {
    color: #FFFFFF;
  }
}

.active-tab-badge {
  background-color: #FFFFFF;
  color: #104D9D;
}
</style>
